/**
 * AlanPlanlariPage.jsx — Salonlar ve Krokiler › Krokiler = ALAN PLANI LİSTESİ (backlog #237, 2026-09-27)
 * Route: /Management/Kroki
 *
 * WAS: bu rota KrokiPage.jsx'i açıyordu — "Salon Krokileri" (salonun Salon Planı'nı, yani duvarları
 * gösteriyordu, Alan Planı'nı değil) ve ÖRNEK VERİYLE çalışan "Kroki Çizim" sekmesi (#193'ün
 * SalonPlaniDuzenlePage'inin kopyası). Dosya duruyor; rotası allRoutes.js'te yorum satırında.
 *
 * Seçili fuara BAĞLI salonlar (salon_to_fuar, GET /fuar/{fuarId}/h-max): her birinin Alan Planı
 * durumu, bağlam için Salon Planı durumu (#234) ve stand sayısı. Seçilen salonun Alan Planı SALT
 * OKUNUR açılır (stand-plan, allowEdit=false) ve altında o salonun standları listelenir; stand
 * başına Stand Detay ve Proje (proje sayfasının rotası yalnızca [1,2,3]).
 * "Saha" eylemi bilinçli olarak yok (Bilge, 2026-09-27): Saha sayfaları stand almıyor; stand başına
 * bir Saha akışı tasarlanınca buraya eklenecek.
 *
 * Düzenleme buradan yapılmaz: "Alan Planına Git" SalonAlanPlaniPage'i açar ve Salon Planı kapısına
 * tabidir (#235/#236). Krokide bir standa tıklamak Stand Detay'ı açar — fdsvector
 * VITE_PARENT_ORIGIN ile derlenene kadar (#184) olay gelmez; liste bu yüzden her standa bağlantı verir.
 */
import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Container, Row, Col, Card, CardHeader, CardBody, Badge, Spinner, Alert, Input, Table, Button,
} from "reactstrap";
import {
  Building2, Search, RefreshCw, Map as MapIcon, Palette, FileText, ExternalLink,
} from "lucide-react";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import MainContext from "../../../Components/Contexts/MainContext";
import KrokiVectorEmbed from "../../../Components/Kroki/KrokiVectorEmbed";
import { KROKI_MODES, resolveStandPlanDocument } from "../../../services/krokiVectorService";
import { getFuarHMax } from "../../../services/fuarKuralService";
import { getSalonlarByFuar } from "../../../services/krokiService";
import { getTumStandlarList } from "../../../services/standlarListService";
// #655 / #658 (2026-11-01): stands with no drawing yet (backend #654), their badges, box and amber marker.
import { getCizilecekStandlar, cizilecekIdKumesi } from "../../../services/cizilecekStandService";
import { CizilecekRozeti, YenidenCizilecekIsareti, CizilecekStandlarKarti } from "../../../Components/FairAreas/cizilecek";
// #302: the link follows the role whose PAGES this login gets (pageAccess.js), like the route itself.
// WAS: import { getCurrentUserGroupId, isStaffAccount } from "../../../services/firmaSessionService";
import { isStaffAccount } from "../../../services/firmaSessionService";
// WAS (until #325): import { getCurrentPageAccessGroupId } from "../../../Routes/pageAccess";
import { useYetki, SAYFA_IZNI } from "../../../services/rbacService";
import {
  CIZIM_DURUM, CizimDurumuPill, alanPlaniEngeli, ALAN_PLANI_ENGEL_KISA,
} from "../../../Components/FairAreas/salonDurumlari";
import { useTranslation } from "react-i18next";
import i18n, { currentLocaleTag } from "../../../i18n";

// WAS (until #325): /Management/Projects/Stands/:standId (StandOverviewDetailPage) bu gruplara açık (allRoutes.js).
// const PROJE_ROTA_GRUPLARI = [1, 2, 3];
// #325: o sayfa page.proje izniyle açılıyor; bağlantı da aynı izne bakar.
const PAGE_SIZE = 500;
const MAX_STANDS = 3000;

const salonSirasi = (a, b) => {
  const an = Number(a.salonName), bn = Number(b.salonName);
  if (!Number.isNaN(an) && !Number.isNaN(bn)) return an - bn;
  return String(a.salonName ?? "").localeCompare(String(b.salonName ?? ""), "tr");
};

export default function AlanPlanlariPage() {
  const { t: tx } = useTranslation("yonetim");
  const navigate = useNavigate();
  const { selectedFuarId } = useContext(MainContext);
  const personel = isStaffAccount();
  // WAS (until #325): const projeAcabilir = PROJE_ROTA_GRUPLARI.includes(getCurrentPageAccessGroupId());
  const projeAcabilir = useYetki(SAYFA_IZNI.PROJE) === true;

  const [ctx, setCtx] = useState(null);           // getFuarHMax
  const [standCounts, setStandCounts] = useState(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [salonSearch, setSalonSearch] = useState("");
  const [seciliSalonId, setSeciliSalonId] = useState(null);

  // Seçili salonun ayrıntısı — { key, probe, stands, truncated }; key = "fuarId|salonId".
  const [detay, setDetay] = useState(null);
  const [standSearch, setStandSearch] = useState("");
  // #655: the To be Drawn box's refresh re-reads the selected hall; the roster's "Çizilecek" chip filters it.
  const [detayToken, setDetayToken] = useState(0);
  const [sadeceCizilecek, setSadeceCizilecek] = useState(false);

  useEffect(() => { document.title = i18n.t("yonetim:alanPlanlari.alanPlanlariFds"); }, []);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    if (!selectedFuarId) return undefined;
    let ignore = false;
    async function run() {
      setLoading(true);
      setError(null);
      try {
        const [hmax, standInfo] = await Promise.all([
          getFuarHMax(selectedFuarId, { forceLive: true }),
          getSalonlarByFuar(selectedFuarId).catch(() => ({ salonlar: [] })),
        ]);
        if (ignore) return;
        setCtx(hmax);
        setStandCounts(new Map((standInfo?.salonlar ?? []).map((s) => [String(s.salonId), s.standCount])));
      } catch (e) {
        if (!ignore) setError(e.message);
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    run();
    return () => { ignore = true; };
  }, [selectedFuarId, reloadToken]);

  const salonlar = useMemo(
    () => [...(ctx?.halls ?? [])].sort(salonSirasi),
    [ctx],
  );
  const filtreliSalonlar = useMemo(() => {
    const q = salonSearch.trim().toLocaleLowerCase("tr");
    if (!q) return salonlar;
    return salonlar.filter((s) => String(s.salonName ?? "").toLocaleLowerCase("tr").includes(q)
      || String(s.salonId).includes(q));
  }, [salonlar, salonSearch]);

  // Varsayılan seçim türetmeyle (state-in-effect yok): seçili yoksa listenin ilki.
  const aktifSalon = useMemo(
    () => salonlar.find((s) => String(s.salonId) === String(seciliSalonId)) || filtreliSalonlar[0] || null,
    [salonlar, filtreliSalonlar, seciliSalonId],
  );
  const aktifSalonId = aktifSalon?.salonId ?? null;
  const detayKey = selectedFuarId && aktifSalonId != null ? `${selectedFuarId}|${aktifSalonId}` : null;

  useEffect(() => {
    if (!detayKey) return undefined;
    let ignore = false;
    const [fuarId, salonId] = detayKey.split("|");
    async function run() {
      try {
        const probePromise = resolveStandPlanDocument({ fairId: fuarId, hallId: salonId });
        const cizilecekPromise = getCizilecekStandlar(fuarId, salonId);   // #655: never throws
        const all = [];
        let page = 1;
        let total = Infinity;
        while (all.length < total && all.length < MAX_STANDS) {
          // eslint-disable-next-line no-await-in-loop
          const res = await getTumStandlarList({ fuarId, salonId, page, limit: PAGE_SIZE });
          if (ignore) return;
          total = res.total ?? 0;
          all.push(...(res.records ?? []));
          if ((res.records ?? []).length < PAGE_SIZE) break;
          page += 1;
        }
        const probe = await probePromise;
        const cizilecek = await cizilecekPromise;
        if (ignore) return;
        all.sort((a, b) => String(a.standName ?? "").localeCompare(String(b.standName ?? ""), "tr", { numeric: true }));
        setDetay({ key: detayKey, probe, stands: all, truncated: all.length < total, error: null, cizilecek });
      } catch (e) {
        if (!ignore) setDetay({ key: detayKey, probe: null, stands: [], truncated: false, error: e.message, cizilecek: null });
      }
    }
    run();
    return () => { ignore = true; };
    // WAS (until #655): [detayKey]
  }, [detayKey, detayToken]);

  const detayHazir = detay && detay.key === detayKey;
  // #655: the roster's "Çizim" column and chip come from the same list as the box, so the two views agree.
  const cizilecekKumesi = useMemo(
    () => (detayHazir && detay.cizilecek?.ok ? cizilecekIdKumesi(detay.cizilecek.stands) : null),
    [detayHazir, detay],
  );
  const standlar = useMemo(() => {
    if (!detayHazir) return [];
    const q = standSearch.trim().toLocaleLowerCase("tr");
    const taban = sadeceCizilecek && cizilecekKumesi ? detay.stands.filter((s) => cizilecekKumesi.has(String(s.standId))) : detay.stands;
    if (!q) return taban;
    return taban.filter((s) => [s.standName, s.firmaName, s.firmaShortName]
      .some((v) => String(v ?? "").toLocaleLowerCase("tr").includes(q)));
  }, [detayHazir, detay, standSearch, sadeceCizilecek, cizilecekKumesi]);

  if (!selectedFuarId) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={tx("alanPlanlari.alanPlanlari")} pageTitle={tx("alanPlanlari.salonlarVeKrokiler")} />
          <Alert color="warning">{tx("alanPlanlari.onceUsttenBirFuarSecin")}</Alert>
        </Container>
      </div>
    );
  }

  const engel = aktifSalon ? alanPlaniEngeli(aktifSalon) : null;

  let kroki;
  if (!aktifSalon) {
    kroki = <div className="text-muted text-center py-5">{tx("alanPlanlari.soldakiListedenBirSalonSecin")}</div>;
  } else if (!detayHazir) {
    kroki = <div className="text-center py-5"><Spinner color="primary" /></div>;
  } else if (detay.probe?.reason === "not-mapped" || detay.probe?.reason === "missing-id") {
    kroki = (
      <Alert color="warning" className="mb-0">
        {tx("alanPlanlari.buSalonunBuFuarIcin")}
      </Alert>
    );
  } else {
    kroki = (
      <>
        {detay.probe && !detay.probe.ok && (
          <Alert color="warning" className="py-2 small">
            {tx("alanPlanlari.planOncedenDogrulanamadiGomuluUygulama", { message: detay.probe.message })}
          </Alert>
        )}
        {aktifSalon.alanPlaniDurum === CIZIM_DURUM.CIZIM_YOK && (
          <Alert color="light" className="py-2 small border">{tx("alanPlanlari.buSalonunAlanPlaniHenuz")}</Alert>
        )}
        <KrokiVectorEmbed
          key={detayKey}
          fairId={selectedFuarId}
          hallId={aktifSalon.salonId}
          hallName={aktifSalon.salonName ?? undefined}
          defaultMode={KROKI_MODES.VIEW}
          allowEdit={false}
          // #653: a hall with nothing drawn yet opens with the (empty) Alan Planı layer ACTIVE and the hall plans passive behind it;
          // once stands exist the look is today's (every layer at its own opacity, no sidebar).
          viewOptions={aktifSalon.alanPlaniDurum === CIZIM_DURUM.CIZIM_YOK
            ? { activeLayer: "stands", forceLabels: true, fit: "hall" }
            : { layers: "none", forceLabels: true, fit: "hall" }}
          height={560}
          onStandClick={(e) => { if (e?.standId) navigate(`/Management/Standlar/${e.standId}`); }}
        />
      </>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={tx("alanPlanlari.alanPlanlari")} pageTitle={tx("alanPlanlari.salonlarVeKrokiler")} />
        {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}

        <Row className="g-3">
          {/* ── Salonlar ── */}
          <Col lg={4}>
            <Card className="h-100">
              <CardHeader className="bg-white border-bottom">
                <div className="d-flex align-items-center justify-content-between">
                  <h5 className="mb-0">
                    <Building2 size={18} className="me-2 text-primary" />
                    {tx("alanPlanlari.salonlar")}{" "}<Badge color="light" className="text-dark ms-1">{salonlar.length}</Badge>
                  </h5>
                  <Button size="sm" color="light" title={tx("common:yenile")} disabled={loading}
                    onClick={() => setReloadToken((t) => t + 1)}>
                    <RefreshCw size={14} />
                  </Button>
                </div>
                <div className="text-muted small mt-1">
                  {ctx?.fuarAlaniName ?? tx("alanPlanlari.fuarAlani")}{" "}{tx("alanPlanlari.fuarBuFuaraBagliSalonlar", { selectedFuarId })}
                </div>
              </CardHeader>
              <CardBody>
                <div className="position-relative mb-3">
                  <Input type="text" placeholder={tx("alanPlanlari.salonAra")} value={salonSearch}
                    onChange={(e) => setSalonSearch(e.target.value)} />
                  <Search size={16} className="text-muted position-absolute" style={{ right: 12, top: 11 }} />
                </div>
                {loading ? (
                  <div className="text-center py-5"><Spinner color="primary" /></div>
                ) : filtreliSalonlar.length === 0 ? (
                  <div className="text-muted text-center py-4">
                    {salonlar.length === 0
                      ? <>{tx("alanPlanlari.buFuaraBagliSalonYok")}{" "}<Link to="/Management/Salonlar">{tx("alanPlanlari.salonlar")}</Link>{" "}{tx("alanPlanlari.sayfasindanAktifEdin")}</>
                      : tx("alanPlanlari.aramanizlaEslesenSalonYok")}
                  </div>
                ) : (
                  <div className="d-flex flex-column gap-2">
                    {filtreliSalonlar.map((s) => {
                      const secili = String(aktifSalon?.salonId) === String(s.salonId);
                      const sayi = standCounts.get(String(s.salonId));
                      return (
                        <div
                          key={s.salonId}
                          role="button"
                          tabIndex={0}
                          onClick={() => { setSeciliSalonId(s.salonId); setStandSearch(""); }}
                          onKeyDown={(e) => { if (e.key === "Enter") { setSeciliSalonId(s.salonId); setStandSearch(""); } }}
                          className={`border rounded-3 p-2 d-flex align-items-center gap-3 ${secili ? "border-primary bg-soft-primary" : ""}`}
                          style={{ cursor: "pointer" }}
                        >
                          <div className={`rounded-3 text-center text-white px-2 py-1 ${secili ? "bg-primary" : "bg-secondary"}`}
                            style={{ minWidth: 46 }}>
                            <div className="fw-bold lh-1">{s.salonName ?? s.salonId}</div>
                          </div>
                          <div className="flex-grow-1">
                            <div className="d-flex align-items-center gap-1 flex-wrap">
                              <CizimDurumuPill id={`ap-liste-alan-${s.salonId}`} plan="alanPlani"
                                durum={s.alanPlaniDurum ?? 0} tarih={s.alanPlaniDurumTarihi} />
                              <YenidenCizilecekIsareti hall={s} />
                              <CizimDurumuPill id={`ap-liste-salon-${s.salonId}`} plan="salonPlani" showLabel={false}
                                durum={s.salonPlaniDurum ?? null} tarih={s.salonPlaniDurumTarihi} />
                            </div>
                            <div className="text-muted d-flex align-items-center gap-2 flex-wrap" style={{ fontSize: 12 }}>
                              {sayi == null ? tx("alanPlanlari.standSayisiYok") : tx("alanPlanlari.stand", { sayi })}
                              <CizilecekRozeti hall={s} />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                <div className="text-muted mt-3" style={{ fontSize: 11 }}>
                  {tx("alanPlanlari.birinciRozetAlanPlaniBu")}
                </div>
              </CardBody>
            </Card>
          </Col>

          {/* ── Seçili salonun Alan Planı + standları ── */}
          <Col lg={8}>
            <Card className="mb-3">
              <CardHeader className="bg-white border-bottom d-flex align-items-center gap-2 flex-wrap">
                <h5 className="mb-0 flex-grow-1">
                  <MapIcon size={18} className="me-2 text-primary" />
                  {aktifSalon ? tx("alanPlanlari.salonAlanPlani", { v: aktifSalon.salonName ?? aktifSalon.salonId }) : tx("alanPlanlari.alanPlani")}
                  <Badge color="light" pill className="ms-2 align-middle text-muted border">{tx("alanPlanlari.saltOkunur")}</Badge>
                </h5>
                {aktifSalon && (
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <CizimDurumuPill id="ap-aktif-alan" plan="alanPlani"
                      durum={aktifSalon.alanPlaniDurum ?? 0} tarih={aktifSalon.alanPlaniDurumTarihi} />
                    <YenidenCizilecekIsareti hall={{ ...aktifSalon, toDrawCount: detayHazir && detay.cizilecek?.ok ? detay.cizilecek.stands.length : aktifSalon.toDrawCount }} />
                    <Link to={`/Management/Salonlar/${aktifSalon.salonId}`} className="btn btn-light btn-sm">{tx("alanPlanlari.salonKarti")}</Link>
                    <Link to={`/Management/Salonlar/${aktifSalon.salonId}/DurumHaritasi`} className="btn btn-soft-primary btn-sm"
                      title={tx("alanPlanlari.standlariIzinBorcDurumunaGore")}>
                      <Palette size={14} className="me-1" />{tx("alanPlanlari.durumHaritasi")}
                    </Link>
                    {/* WAS (until #653): a DISABLED button with the reason as a tooltip while the Salon Planı was unfinished -
                        {personel && (engel ? (<span title={engel}><span className="btn btn-soft-info btn-sm border disabled" aria-disabled="true">…Alan Planına Git</span></span>) : (<Link …>Alan Planına Git</Link>))}
                        Now always a link: the page it opens shows the reason (and the plan read-only) while the Salon Planı is unfinished. The status locks stay (#236). */}
                    {personel && (
                      <Link to={`/Management/Salonlar/${aktifSalon.salonId}/AlanPlani`} className="btn btn-soft-info btn-sm border"
                        title={engel ?? tx("alanPlanlari.alanPlaniNiDuzenlemedeAc")}>
                        <MapIcon size={14} className="me-1" />{tx("alanPlanlari.alanPlaninaGit")}
                      </Link>
                    )}
                  </div>
                )}
              </CardHeader>
              <CardBody>
                {personel && engel && (
                  <div className="small text-warning mb-2" title={engel}>{ALAN_PLANI_ENGEL_KISA}: {engel}</div>
                )}
                {kroki}
              </CardBody>
            </Card>

            {/* #655: the stands of this hall that have no drawing yet (staff only: this whole route is) */}
            {aktifSalon && (
              <CizilecekStandlarKarti
                hall={aktifSalon}
                salonId={aktifSalon.salonId}
                fuarAlaniId={ctx?.fuarAlaniId ?? null}
                liste={detayHazir ? detay.cizilecek : null}
                yukleniyor={!detayHazir}
                onYenile={() => setDetayToken((n) => n + 1)}
                duzenleyebilir={personel}
                alanPlaniEngelli={engel}
                localeTag={currentLocaleTag()}
              />
            )}

            {aktifSalon && (
              <Card>
                <CardHeader className="d-flex align-items-center gap-2 flex-wrap py-2">
                  <span className="fw-semibold flex-grow-1">
                    {tx("alanPlanlari.standlar")}{" "}{detayHazir && <Badge color="light" className="text-dark ms-1">{detay.stands.length}</Badge>}
                  </span>
                  {cizilecekKumesi && (
                    <Button size="sm" color={sadeceCizilecek ? "warning" : "light"} onClick={() => setSadeceCizilecek((v) => !v)} aria-pressed={sadeceCizilecek}>
                      {tx("cizilecek.cizilecekFiltre", { sayi: cizilecekKumesi.size })}
                    </Button>
                  )}
                  <div className="position-relative" style={{ minWidth: 220 }}>
                    <Input bsSize="sm" type="text" placeholder={tx("alanPlanlari.standFirmaAra")} value={standSearch}
                      onChange={(e) => setStandSearch(e.target.value)} />
                  </div>
                </CardHeader>
                <CardBody className="p-0" style={{ maxHeight: 520, overflowY: "auto" }}>
                  {!detayHazir ? (
                    <div className="text-center py-4"><Spinner color="primary" /></div>
                  ) : detay.error ? (
                    <Alert color="danger" className="m-3">{detay.error}</Alert>
                  ) : standlar.length === 0 ? (
                    <div className="text-muted text-center py-4">
                      {detay.stands.length === 0 ? tx("alanPlanlari.buSalondaBuFuarIcin") : tx("alanPlanlari.aramanizlaEslesenStandYok")}
                    </div>
                  ) : (
                    <Table size="sm" className="align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th className="ps-3">{tx("alanPlanlari.stand2")}</th>
                          <th>{tx("alanPlanlari.katilimci")}</th>
                          {cizilecekKumesi && <th>{tx("cizilecek.cizimSutunu")}</th>}
                          <th className="text-end pe-3">{tx("alanPlanlari.eylemler")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {standlar.map((s) => (
                          <tr key={s.standId}>
                            <td className="ps-3 fw-semibold">{s.standName ?? `#${s.standId}`}</td>
                            <td className="small">{s.firmaShortName || s.firmaName || <span className="text-muted">—</span>}</td>
                            {cizilecekKumesi && (
                              <td>
                                {cizilecekKumesi.has(String(s.standId))
                                  ? <Badge color="warning" pill>{tx("cizilecek.cizilecekDurum")}</Badge>
                                  : <Badge color="success" pill>{tx("cizilecek.cizildi")}</Badge>}
                              </td>
                            )}
                            <td className="text-end pe-3">
                              <div className="d-inline-flex gap-1">
                                <Link to={`/Management/Standlar/${s.standId}`} className="btn btn-sm btn-light" title={tx("alanPlanlari.standDetay")}>
                                  <ExternalLink size={12} className="me-1" />{tx("alanPlanlari.standDetay")}
                                </Link>
                                {projeAcabilir && (
                                  <Link to={`/Management/Projects/Stands/${s.standId}`} className="btn btn-sm btn-soft-secondary" title={tx("alanPlanlari.proje")}>
                                    <FileText size={12} className="me-1" />{tx("alanPlanlari.proje")}
                                  </Link>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                  {detayHazir && detay.truncated && (
                    <div className="text-muted small p-2">{tx("alanPlanlari.ilkStandGosteriliyor", { length: detay.stands.length })}</div>
                  )}
                </CardBody>
              </Card>
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
}
